import {useFlux, useFluxListener} from '@nlabs/arkhamjs-utils-react';
import {POST_CONSTANTS} from '@nlabs/metropolisjs/stores';
import {getGraphql} from '@nlabs/metropolisjs/utils';
import {useEffect} from 'react';

import {readArkhamArticles} from '../actions/articleActions';
import {
  loadStoredArticles,
  parseArticlePosts,
  publicArticleEndpoint,
  type SentinelArticlesResponse
} from '../services/articles/reaktorArticleClient';
import type {Article} from '../types/article';

const articleLoadRequests = new Map<string, Promise<Article[]>>();

export const SiteLoadArticleFeed = () => {
  const flux = useFlux();
  const endpoint = publicArticleEndpoint(window.location.hostname);

  useFluxListener(POST_CONSTANTS.GET_LIST_SUCCESS, ({list}: {readonly list?: unknown}) => {
    try {
      const articles = parseArticlePosts(list);
      void flux.dispatch({articles, status: 'live', type: 'ARTICLE_FEED_REPLACE'});
    } catch {
      void flux.dispatch({articles: [], status: 'error', type: 'ARTICLE_FEED_REPLACE'});
    }
  });

  useFluxListener(POST_CONSTANTS.GET_LIST_ERROR, () => {
    void flux.dispatch({articles: [], status: 'error', type: 'ARTICLE_FEED_REPLACE'});
  });

  useEffect(() => {
    let active = true;
    if(readArkhamArticles().length) return () => { active = false; };
    const existingRequest = articleLoadRequests.get(endpoint);
    const request = existingRequest ?? loadStoredArticles({
      endpoint,
      post: async (requestEndpoint, body) => {
        const data = await getGraphql(flux, requestEndpoint, false, body, {
          onSuccess: async (incomingData) => {
            const posts = incomingData?.posts;
            if(
              Array.isArray(posts?.articles)
              && Array.isArray(posts.pdArticles)
              && Array.isArray(posts.legacyPdArticles)
            ) {
              await flux.dispatch({
                list: [...posts.articles, ...posts.pdArticles, ...posts.legacyPdArticles],
                type: POST_CONSTANTS.GET_LIST_SUCCESS
              });
            }
            return incomingData;
          }
        }) as NonNullable<SentinelArticlesResponse['data']>;
        return {data};
      }
    });
    if(!existingRequest) articleLoadRequests.set(endpoint, request);

    void request.catch((error) => {
      articleLoadRequests.delete(endpoint);
      if(active) void flux.dispatch({error, type: POST_CONSTANTS.GET_LIST_ERROR});
    });

    return () => { active = false; };
  }, [endpoint, flux]);

  return null;
};
